import React, { FC } from 'react';
import { Form } from 'tea-component';
import { getAdditionalArr } from './utils/formatter';
import CheckboxGroup from './components/common/CheckboxGroup';
import RadioGroup from './components/common/RadioGroup';
import SingleText from './components/common/SingleText';
import Cascader from './components/common/Cascader';
import Switch from './components/common/Switch';
import InputNumber from './components/common/InputNumber';
import DatePicker from './components/common/DatePicker';
import RegionSelector from './components/business/RegionSelector';
import RegionCountrySelector from '@intelligence/components/RegionCountrySelector';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { getShowLabel, parseAndEvaluate } from './utils';
import { IChildProp, IFormContentProps } from './type';
import MutipleSelector from './components/common/MutipleSelector';
import TextGroup from './components/common/TextGroup';
import useMarketFilterApi from '@/pages/intelligence/hooks/useMarketSelector';
import { MarketSpecailType } from '@intelligence/hooks/useMarketSelector';

const FormContent: FC<IFormContentProps> = (props) => {
  const { formList, addFields, formValues, onChange, tableParams } = props;
  const { formatMessage } = useIntlExtend();
  // console.log('FormContentFormContent', props);
  const { regionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.metricTable,
  });

  /**
   * 只对普通类型的change回调
   * @param field 当前需要改变的字段key
   * @param value 值
   */
  const handleChange = (field: string, value: any) => {
    onChange(field, value);
  };

  /**
   * 针对subForm类型的change回调
   * @param key 父字段key
   * @param field 当前需要改变的字段key
   * @param value 值
   */
  const handleSubChange = (key: string, field: string, value: any) => {
    onChange(key, {
      [field]: value,
    });
  };

  const getFormComp = (item: IChildProp) => {
    const { value = '', type, children = [], msg = '', isShowOneText } = item || {};
    switch (type) {
      case 'text':
      case 'dynamicText':
        // TODO 多个的样式间距未调试
        return children.map((child) => <SingleText key={child?.value} formItem={child} />);
      case 'checkboxGroup': {
        // isShowOneText表示表示，当只有一个选项时显示文本
        if (isShowOneText && formValues[value]?.length === 1) {
          const find = children.find((item) => item.value === formValues[value]?.[0]);
          if (find) return <SingleText formItem={find} />;
        }
        return (
          <CheckboxGroup
            options={getAdditionalArr(children, item, formValues, addFields)}
            value={formValues[value]}
            field={value}
            onChange={(field, value) => handleChange(field, value)}
            msg={msg}
          />
        );
      }
      case 'radio':
        if (children?.length === 1) {
          // TODO 待测试
          return <SingleText formItem={children?.[0]} />;
        }
        return (
          <RadioGroup
            options={getAdditionalArr(children, item, formValues, addFields)}
            value={formValues[value]}
            field={value}
            onChange={(field, value) => handleChange(field, value)}
            msg={msg}
          />
        );
      case 'subForm':
        return (
          <Form className="dbi-form-item--subForm">
            <FormContent
              formList={children}
              addFields={addFields}
              formValues={{
                ...formValues[value],
                // 把父级的值绑定到子级上，方便config上面进行联动显隐
                parent: {
                  ...formValues,
                  ...addFields,
                },
              }}
              tableParams={tableParams}
              onChange={(subField: string, subValue: any) =>
                handleSubChange(value, subField, subValue)
              }
            />
          </Form>
        );
      case 'cascader':
        return (
          <Cascader
            hasSelectAll={!!item.hasSelectAll}
            hasSearch={!!item.hasSearch}
            hasShortcut={!!item.hasShortcut}
            mainCategory={item.mainCategory}
            subCategory={item.subCategory}
            shortcutOptions={item.shortcutOptions}
            displaySearchParentText={item.displaySearchParentText}
            searchTagAutoWidth={item.searchTagAutoWidth}
            searchTagWidth={item.searchTagWidth}
            shortcutDefaultValue={item.shortcutDefaultValue || ''}
            options={getAdditionalArr(children, item, formValues, addFields)}
            value={formValues[value]}
            field={value}
            hasHighlight={true}
            onChange={(field: string, value) => {
              handleChange(field, value);
            }}
            {...item}
          />
        );
      case 'switch':
        return (
          <Switch
            value={formValues[value]}
            field={value}
            onChange={(field: string, value: boolean) => handleChange(field, value)}
          />
        );
      case 'inputNumber': {
        const { total, maxValue } = addFields?.additionalFields || {};
        return (
          <InputNumber
            value={formValues[value]}
            field={value}
            total={total}
            onChange={(field: string, value: boolean) => handleChange(field, value)}
          />
        );
      }
      case 'datePicker':
        return (
          <DatePicker
            value={formValues[value]}
            field={value}
            onChange={(field: string, value: boolean) => handleChange(field, value)}
            formItem={item}
          />
        );
      case 'mutipleCountry':
        return (
          <RegionSelector
            value={formValues[value]}
            field={value}
            onChange={(field: string, value: boolean) => handleChange(field, value)}
            formItem={item}
          />
        );
      case 'regionCountrySelectMultiple':
        return (
          <RegionCountrySelector
            key={'region_country_selector'}
            onChange={(field: string, value: boolean) => handleChange(field, value)}
            value={formValues[value]}
            countryOptions={regionOptions}
            selectType={'multiple'}
            field={'regionCountrySelectMultiple'}
            defaultValue={
              tableParams ? { type: tableParams?.region_type, value: [tableParams?.region] } : {}
            }
            isAll={false}
          />
        );
      case 'mutipleSelect':
        return (
          <MutipleSelector
            options={getAdditionalArr(children, item, formValues, addFields)}
            value={formValues[value]}
            field={value}
            onChange={(field: string, value) => handleChange(field, value)}
          />
        );
      case 'textGroup':
        return (
          <TextGroup
            options={getAdditionalArr(children, item, formValues, addFields)}
            field={value}
          />
        );
      default:
        return '';
    }
  };

  const render = () => {
    const renderArr: any = [];
    formList?.forEach((formItem: any) => {
      const { condition } = formItem;
      // 根据condition字段的条件进行显示隐藏
      const isShow = condition
        ? parseAndEvaluate(condition, {
            ...formValues,
            ...addFields,
          })
        : true;
      Object.assign(formItem, { isShow });
      if (isShow) {
        renderArr.push(
          <Form.Item
            key={formItem.value}
            label={getShowLabel(formItem, formatMessage)}
            showStatusIcon={false}
            className={formItem.className}
          >
            {getFormComp(formItem)}
          </Form.Item>,
        );
      }
    });
    return renderArr;
  };

  return <>{render()}</>;
};

export default FormContent;
